import * as React from 'react';
import {useState, useMemo, useRef} from 'react';
import {createViewConfig, view, assignProps} from '@sail/engine';
import type {View} from '../../view';
import {FormFieldGroup} from '../FormFieldGroup';
import {TextField} from '../TextField';
import {SearchField} from '../SearchField';
import {Select} from '../Select';
import type {AddressFieldSchema, Subregion} from '../addressData/schemaTypes';
import {addressFieldLabels as defaultFieldLabels} from '../addressData/addressFieldLabels';
import type {FieldLabelOverrides} from '../addressData/addressFieldLabels';
import {deepMerge} from '../../utils';

import addressSchemasJSON from '../addressData/addressSchemas.json';
import defaultCountryLabels from '../addressData/countryLabels.json';

export {defaultCountryLabels, addressSchemasJSON};

export type AddressFieldCountryCode = keyof typeof addressSchemasJSON;

const addressSchemas = addressSchemasJSON as Record<
  AddressFieldCountryCode,
  AddressFieldSchema
>;

const COUNTRIES = Object.keys(addressSchemas) as AddressFieldCountryCode[];

function getSubregionName(subregion: Subregion) {
  return subregion.latinName
    ? `${subregion.name} - ${subregion.latinName}`
    : subregion.name;
}

type AddressFields<T> = {
  /**
   * A two-letter international country code (ISO 3166-1 alpha-2), e.g. "US"
   */
  countryCode: T;

  /**
   * A street address which fully identifies the location of an address within its district
   */
  address: T;

  /**
   * An optional second line of a street address
   */
  address2: T;

  /**
   * State, county, or province
   */
  subregion: T;

  /**
   * City, district, suburb, town, or village
   */
  locality: T;

  /**
   * Neighborhood, township, district, or suburb
   */
  dependentLocality: T;

  /**
   * A postal code, such as ZIP in the United States
   */
  postalCode: T;

  /**
   * A code used to route or disambiguate addresses within a locality
   */
  sortingCode: T;
};

/**
 * The internal representation of address field values.
 * All fields except `countryCode` are optional, though certain country schemas may
 * call for runtime validation to ensure presence of certain fields.
 */
export type AddressValue = {countryCode: AddressFieldCountryCode} & Partial<
  Omit<AddressFields<string>, 'countryCode'>
>;

/**
 * This interface provides a way to transform field values between the `AddressValue` interface
 * and a custom interface that aligns with an external data model.
 * The `unsupportedFields` property can be used to omit fields that aren't represented
 * by the external data model.
 */
export type AddressValueAdapter<T> = {
  /**
   * A function that transforms the provided value type into the expected internal representation.
   */
  toAddressValue: (value: T) => AddressValue;
  /**
   * A function that transforms the value's internal representation back into the provided value type.
   */
  fromAddressValue: (value: AddressValue) => T;
  /**
   * A list of field types not supported by the data model.
   * Including a field type in this list will prevent it from being shown.
   */
  unsupportedFields?: (keyof AddressFields<string>)[];
};

export type AddressAutocompleteItem = {
  address: string;
  addressValue: AddressValue;
};

export type SubregionOverride = Partial<
  Record<keyof typeof defaultCountryLabels, Record<Subregion['key'], string>>
>;

const DEFAULT_ADAPTER: AddressValueAdapter<AddressValue> = {
  toAddressValue: (value: AddressValue) => value,
  fromAddressValue: (value: AddressValue) => value,
};

type AddressFieldProps<T = AddressValue> = Omit<
  View.IntrinsicElement<'fieldset'>,
  'defaultValue' | 'onChange'
> & {
  /**
   * The set of values for the corresponding fields when being used as a controlled component; used in conjunction with `onChange`
   */
  value?: T;

  /**
   * The set of values the component should be initialized with when being used as an uncontrolled component
   */
  defaultValue?: T;

  /**
   * The callback function to fire when the value changes
   */
  onChange?: (value: T) => void;

  /**
   * An adapter to transform the value type to and from the expected internal type to one compatible with another data model
   */
  adapter: AddressValueAdapter<T>;

  /**
   * A list of two-letter international country codes (ISO 3166-1 alpha-2, e.g. "US") that are selectable from the country selector
   */
  countryAllowlist?: AddressFieldCountryCode[];

  /**
   * A list of two-letter international country codes (ISO 3166-1 alpha-2, e.g. "US") that should be excluded from the country selector.
   * Note that this will override `countryAllowlist` if there's a conflict.
   */
  countryDenylist: AddressFieldCountryCode[];

  /**
   * Text that describes the control. Will be both visible and clickable.
   */
  label?: React.ReactNode;

  /**
   * Descriptive text that will be rendered adjacent to the control's label
   */
  description?: React.ReactNode;

  /**
   * Error text that will be rendered above the control
   */
  error?: React.ReactNode;

  /**
   * Disables the field and removes it from submission
   */
  disabled?: boolean;

  /**
   * Makes the field non-editable
   */
  readOnly?: boolean;

  /**
   * Enable or disable default browser autocomplete. Defaults to true.
   */
  autoComplete: boolean;

  /**
   * Sets the size of the field
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Hides the country selector when only 1 country is valid
   */
  preferHideCountrySelector?: boolean;

  /**
   * A function for retrieving a list of address for autocompletion
   */
  onAutocomplete?: (value: string) => Promise<Array<AddressAutocompleteItem>>;

  /**
   * Custom labels, useful for localization. API subject to change!
   */
  unstable_labelsOverrides: {
    address?: FieldLabelOverrides;
    country?: Partial<typeof defaultCountryLabels>;
    subregion?: SubregionOverride;
  };

  subviews?: View.Subviews<{
    countryCode: typeof Select;
    address1: typeof SearchField<AddressAutocompleteItem & {id: string}>;
    address2: typeof TextField;
    subregion: typeof Select;
    locality: typeof TextField;
    dependentLocality: typeof TextField;
    postalCode: typeof TextField;
    sortingCode: typeof TextField;
  }>;
};

function filterValues<T extends Record<string, string>>(
  obj: T,
  callback: (key: keyof T, obj: T) => boolean,
): T {
  return (Object.keys(obj) as (keyof T)[]).reduce((memo, key) => {
    if (callback(key, obj)) {
      memo[key] = obj[key];
    }
    return memo;
  }, {} as T);
}

export const AddressFieldConfig = createViewConfig({
  // we need to be able to accept an arbitrary type for the adapter
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: {} as AddressFieldProps<any>,
  defaults: {
    countryDenylist: [],
    adapter: DEFAULT_ADAPTER,
    autoComplete: true,
    preferHideCountrySelector: false,
    unstable_labelsOverrides: {},
  },
  flattens: true,
});

export const AddressField = AddressFieldConfig.createGenericView(function <
  T extends Record<string, string>,
>({
  label,
  value: valueProp,
  defaultValue,
  onChange,
  countryAllowlist = COUNTRIES,
  countryDenylist,
  adapter,
  readOnly,
  autoComplete,
  preferHideCountrySelector,
  onAutocomplete,
  unstable_labelsOverrides,
  subviews,
  ...props
}: View.RenderProps<AddressFieldProps<T>>) {
  const {
    country: countryLabelsOverrides = {},
    address: addressLabelsOverrides = {},
    subregion: subregionLabelsOverrides = {} as SubregionOverride,
  } = unstable_labelsOverrides;

  const countryLabels = useMemo(
    () => ({...defaultCountryLabels, ...countryLabelsOverrides}),
    [countryLabelsOverrides],
  );

  const countries = useMemo(
    () =>
      countryAllowlist
        .slice()
        .filter((country) => !countryDenylist.includes(country))
        .sort((a, b) => countryLabels[a].localeCompare(countryLabels[b])),
    [countryAllowlist, countryDenylist, countryLabels],
  );

  const [uncontrolledValue, setUncontrolledValue] = useState<T>(
    defaultValue ?? {
      // @ts-expect-error 'countryCode' does not exist in type '() => T'
      countryCode: countries.length === 1 ? countries[0] : 'US',
    },
  );

  const addressValue = useMemo(() => {
    const adaptedValue = adapter.toAddressValue(valueProp ?? uncontrolledValue);
    return {
      ...adaptedValue,
      // ensure that countryCode is always set
      countryCode:
        adaptedValue.countryCode ??
        valueProp?.countryCode ??
        uncontrolledValue?.countryCode,
    };
  }, [adapter, valueProp, uncontrolledValue]);

  const countryCode = addressValue.countryCode;

  const schema =
    useMemo(() => addressSchemas[countryCode], [countryCode]) ?? [];

  const unsupportedFields = new Set(adapter.unsupportedFields);

  const updateValue = React.useCallback(
    (value: T) => {
      const filteredValue = filterValues(value, (key, obj) =>
        Boolean(obj[key]),
      );

      onChange?.(filteredValue);
      setUncontrolledValue(filteredValue);
    },
    [onChange],
  );

  const [autocompleteItems, setAutocompleteItems] = useState<
    Array<AddressAutocompleteItem & {id: string}>
  >([]);
  const latestAutocompletePromise = useRef<Promise<
    Array<AddressAutocompleteItem>
  > | null>(null);
  const line1Focused = useRef<boolean>(false);
  const [isAutocompleteOpen, setAutocompleteOpen] = useState(false);

  function clearAutocompleteRequest() {
    latestAutocompletePromise.current = null;
  }

  async function runAutocomplete(value: string) {
    if (
      onAutocomplete &&
      // We only want to run this if the input is still focused. The input value
      // can change without being focused due to browser extension autocompletions,
      // and we don't want to show our autocompletions if that happens
      line1Focused.current
    ) {
      if (value === '') {
        clearAutocompleteRequest();
        setAutocompleteItems([]);
        setAutocompleteOpen(false);
      } else {
        const promise = onAutocomplete(value);
        latestAutocompletePromise.current = promise;
        const items = await promise;

        // Only ever use the results from the latest request
        if (latestAutocompletePromise.current === promise) {
          setAutocompleteItems(
            items.map((item) => ({id: item.address, ...item})),
          );
          setAutocompleteOpen(items.length > 0);
        }
      }
    }
  }

  if (!schema) {
    throw new Error(
      `No address schema found for country code "${countryCode}"`,
    );
  }
  const showCountrySelector = useMemo(
    () => !preferHideCountrySelector || countries.length > 1,
    [countries.length, preferHideCountrySelector],
  );

  const addressFieldLabels = deepMerge(
    defaultFieldLabels,
    addressLabelsOverrides as Partial<typeof defaultFieldLabels>,
  );

  return (
    <FormFieldGroup {...props} legend={label} layout="vertical">
      {showCountrySelector ? (
        <Select
          data-testid="country"
          inherits={subviews.countryCode}
          label={addressFieldLabels.country}
          value={countryCode}
          onChange={(e) => {
            const nextAddressFieldCountryCode = e.target
              .value as AddressFieldCountryCode;
            const nextAddressValue: AddressValue = {
              countryCode: nextAddressFieldCountryCode,
            };
            addressSchemas[nextAddressFieldCountryCode]?.forEach((field) => {
              switch (field.type) {
                case 'address':
                  nextAddressValue.address = addressValue.address;
                  nextAddressValue.address2 = addressValue.address2;
                  break;
                case 'subregion':
                  nextAddressValue.subregion = addressValue.subregion;
                  break;
                case 'locality':
                  nextAddressValue.locality = addressValue.locality;
                  break;
                case 'dependent-locality':
                  nextAddressValue.dependentLocality =
                    addressValue.dependentLocality;
                  break;
                case 'postal-code':
                  nextAddressValue.postalCode = addressValue.postalCode;
                  break;
                case 'sorting-code':
                  nextAddressValue.sortingCode = addressValue.sortingCode;
                  break;
                default:
                  break;
              }
            });

            updateValue(adapter.fromAddressValue(nextAddressValue));
          }}
          hiddenElements={['label']}
          disabled={countries.length <= 1 || readOnly}
          required
          autoComplete={autoComplete === true ? 'country' : 'off'}
        >
          <view.option value="" css={{display: 'none'}}>
            {addressFieldLabels.country}
          </view.option>
          {!countries.includes(countryCode) ? (
            <option key={countryCode} value={countryCode} disabled>
              {countryLabels[countryCode]}
            </option>
          ) : null}
          {countries.map((countryCode) => (
            <option key={countryCode} value={countryCode}>
              {countryLabels[countryCode]}
            </option>
          ))}
        </Select>
      ) : null}

      {schema.map((field, index) => {
        if (field.type === 'address' && !unsupportedFields.has('address')) {
          return (
            <React.Fragment key="address">
              <SearchField
                inherits={subviews.address1}
                data-testid="address1"
                key="address1"
                label={addressFieldLabels.address_line1}
                placeholder={addressFieldLabels.address_line1}
                value={addressValue.address ?? ''}
                defaultFilter={false}
                // There is never a selected value. We fully control the value of this field
                // which is just a raw text value
                selectedKey={null}
                data={autocompleteItems}
                item="address"
                onInputChange={(address: string) => {
                  updateValue(
                    adapter.fromAddressValue({
                      ...addressValue,
                      address,
                    }),
                  );
                  runAutocomplete(address);
                }}
                isOpen={isAutocompleteOpen}
                onOpenChange={(open) => {
                  // We never want SearchField to open it, we only want to respect
                  // its close behaviors
                  if (!open) {
                    setAutocompleteOpen(open);
                  }
                }}
                onSelectionChange={(id) => {
                  const item = autocompleteItems?.find((s) => s.id === id);
                  if (item) {
                    updateValue(adapter.fromAddressValue(item.addressValue));
                  }
                  setAutocompleteOpen(false);
                }}
                hiddenElements={['label']}
                readOnly={readOnly}
                required={field.required}
                subviews={{
                  input: {
                    uses: [
                      assignProps({
                        type: 'text',
                        autoComplete:
                          autoComplete === true ? 'address-line1' : 'off',
                        onFocus: () => {
                          line1Focused.current = true;
                        },
                        onBlur: () => {
                          line1Focused.current = false;
                          clearAutocompleteRequest();
                        },
                      }),
                    ],
                  },
                  searchIcon: {css: {display: 'none'}},
                  cancelAction: {css: {display: 'none'}},
                }}
              />
              <TextField
                data-testid="address2"
                inherits={subviews.address2}
                key="address2"
                label={addressFieldLabels.address_line2}
                placeholder={addressFieldLabels.address_line2}
                value={addressValue.address2 ?? ''}
                onChange={(e) => {
                  const address2 = e.target.value;
                  updateValue(
                    adapter.fromAddressValue({
                      ...addressValue,
                      address2,
                    }),
                  );
                }}
                hiddenElements={['label']}
                readOnly={readOnly}
                autoComplete={autoComplete === true ? 'address-line2' : 'off'}
              />
            </React.Fragment>
          );
        } else if (
          field.type === 'subregion' &&
          !unsupportedFields.has('subregion') &&
          field.schema.list.length
        ) {
          return (
            <Select
              data-testid={field.type}
              inherits={subviews.subregion}
              key={field.type}
              label={addressFieldLabels.subregion[field.schema.nameType]}
              placeholder={addressFieldLabels.subregion[field.schema.nameType]}
              value={addressValue.subregion ?? ''}
              onChange={(e) => {
                const subregion = e.target.value;
                updateValue(
                  adapter.fromAddressValue({
                    ...addressValue,
                    subregion,
                  }),
                );
              }}
              hiddenElements={['label']}
              disabled={readOnly}
              required={field.required}
              autoComplete={autoComplete === true ? 'address-level1' : 'off'}
            >
              <view.option value="" css={{display: 'none'}}>
                {addressFieldLabels.subregion[field.schema.nameType]}
              </view.option>
              {field.schema.list.map((subregion) => {
                const subregionOverride =
                  subregionLabelsOverrides[countryCode]?.[subregion.key];

                return (
                  <option key={subregion.key} value={subregion.key}>
                    {subregionOverride ?? getSubregionName(subregion)}
                  </option>
                );
              })}
            </Select>
          );
        } else if (
          field.type === 'locality' &&
          !unsupportedFields.has('locality')
        ) {
          return (
            <TextField
              data-testid={field.type}
              inherits={subviews.locality}
              key={field.type}
              label={addressFieldLabels.locality[field.schema.nameType]}
              placeholder={addressFieldLabels.locality[field.schema.nameType]}
              value={addressValue.locality ?? ''}
              onChange={(e) => {
                const locality = e.target.value;
                updateValue(
                  adapter.fromAddressValue({
                    ...addressValue,
                    locality,
                  }),
                );
              }}
              hiddenElements={['label']}
              readOnly={readOnly}
              required={field.required}
              autoComplete={autoComplete === true ? 'address-level2' : 'off'}
            />
          );
        } else if (
          field.type === 'dependent-locality' &&
          !unsupportedFields.has('dependentLocality')
        ) {
          return (
            <TextField
              inherits={subviews.dependentLocality}
              key={field.type}
              data-testid={field.type}
              label={
                addressFieldLabels.dependentLocality[field.schema.nameType]
              }
              placeholder={
                addressFieldLabels.dependentLocality[field.schema.nameType]
              }
              value={addressValue.dependentLocality ?? ''}
              onChange={(e) => {
                const dependentLocality = e.target.value;
                updateValue(
                  adapter.fromAddressValue({
                    ...addressValue,
                    dependentLocality,
                  }),
                );
              }}
              hiddenElements={['label']}
              readOnly={readOnly}
              required={field.required}
              autoComplete={autoComplete === true ? 'address-level3' : 'off'}
            />
          );
        } else if (
          field.type === 'postal-code' &&
          !unsupportedFields.has('postalCode')
        ) {
          return (
            <TextField
              data-testid={field.type}
              inherits={subviews.postalCode}
              key={field.type}
              label={addressFieldLabels.postalCode[field.schema.nameType]}
              placeholder={addressFieldLabels.postalCode[field.schema.nameType]}
              value={addressValue.postalCode ?? ''}
              onChange={(e) => {
                const postalCode = e.target.value;
                updateValue(
                  adapter.fromAddressValue({
                    ...addressValue,
                    postalCode,
                  }),
                );
              }}
              hiddenElements={['label']}
              readOnly={readOnly}
              required={field.required}
              autoComplete={autoComplete === true ? 'postal-code' : 'off'}
            />
          );
        } else if (
          field.type === 'sorting-code' &&
          !unsupportedFields.has('sortingCode')
        ) {
          return (
            <TextField
              data-testid={field.type + index}
              inherits={subviews.sortingCode}
              // Cote d'Ivoire has two sorting code fields
              // so we need to differentiate their keys with an index
              // eslint-disable-next-line react/no-array-index-key
              key={field.type + index}
              label={addressFieldLabels.sortingCode[field.schema.nameType]}
              placeholder={
                addressFieldLabels.sortingCode[field.schema.nameType]
              }
              value={addressValue.sortingCode ?? ''}
              onChange={(e) => {
                const sortingCode = e.target.value;
                updateValue(
                  adapter.fromAddressValue({
                    ...addressValue,
                    sortingCode,
                  }),
                );
              }}
              hiddenElements={['label']}
              readOnly={readOnly}
              required={field.required}
            />
          );
        }

        return null;
      })}

      {Object.entries(uncontrolledValue).map(([name, value]) =>
        value ? (
          <input
            data-testid={name}
            key={name}
            type="hidden"
            name={name}
            value={value}
          />
        ) : null,
      )}
    </FormFieldGroup>
  );
});
